import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Receipt,
  Add,
  Delete,
  CheckCircle,
  Warning,
  Save,
  Send,
} from '@mui/icons-material';
import { financeService, Account, CreateJournalEntryData } from '../../services/financeService';
import { formatCurrency } from '../../utils/currency';

interface QuickJournalEntryProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (entry: any) => void;
}

interface JournalLine {
  account: Account | null;
  description: string;
  debit_amount: number;
  credit_amount: number;
}

const emptyLine: JournalLine = {
  account: null,
  description: '',
  debit_amount: 0,
  credit_amount: 0,
};

const accountTypeColors: Record<string, string> = {
  ASSET: '#4caf50',
  LIABILITY: '#f44336',
  EQUITY: '#9c27b0',
  INCOME: '#2196f3',
  EXPENSE: '#ff9800',
};

const QuickJournalEntry: React.FC<QuickJournalEntryProps> = ({ open, onClose, onSuccess }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [lines, setLines] = useState<JournalLine[]>([{ ...emptyLine }, { ...emptyLine }]);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch accounts on mount
  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const data = await financeService.getAccounts({ isActive: true });
      setAccounts(data.accounts);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts. Please try again.');
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchAccounts();
    }
  }, [open, fetchAccounts]);

  // Calculate totals
  const calculateTotals = () => {
    let totalDebit = 0;
    let totalCredit = 0;

    lines.forEach(line => {
      totalDebit += line.debit_amount || 0;
      totalCredit += line.credit_amount || 0;
    });

    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    const difference = totalDebit - totalCredit;

    return { totalDebit, totalCredit, isBalanced, difference };
  };

  const totals = calculateTotals();

  // Add new line
  const addLine = () => {
    setLines([...lines, { ...emptyLine }]);
  };

  // Remove line
  const removeLine = (index: number) => {
    if (lines.length > 2) {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  // Update line - each line can only have debit OR credit, not both
  const updateLine = (index: number, field: keyof JournalLine, value: any) => {
    const newLines = [...lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setLines(newLines);
  };

  // Submit journal entry
  const handleSubmit = async (status: 'DRAFT' | 'POSTED') => {
    // Validation
    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    const validLines = lines.filter(line =>
      line.account && (line.debit_amount > 0 || line.credit_amount > 0)
    );

    if (validLines.length < 2) {
      setError('Journal entry must have at least 2 lines with amounts');
      return;
    }

    if (!totals.isBalanced) {
      setError('Debits and Credits must be equal');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const entryData: CreateJournalEntryData = {
        entryDate,
        description,
        reference: reference || undefined,
        status,
        entries: validLines.map(line => ({
          accountId: line.account!.id,
          description: line.description || description,
          debitAmount: line.debit_amount || 0,
          creditAmount: line.credit_amount || 0,
        })),
      };

      const entry = await financeService.createJournalEntry(entryData);
      onSuccess?.(entry);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create journal entry');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setLines([{ ...emptyLine }, { ...emptyLine }]);
    setEntryDate(new Date().toISOString().split('T')[0]);
    setDescription('');
    setReference('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt color="primary" />
          <Typography variant="h6">Journal Entry</Typography>
          {totals.isBalanced ? (
            <Chip
              icon={<CheckCircle />}
              label="Balanced"
              color="success"
              size="small"
              sx={{ ml: 2 }}
            />
          ) : (
            <Chip
              icon={<Warning />}
              label={`Unbalanced: ${formatCurrency(Math.abs(totals.difference))}`}
              color="error"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Header Info */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Entry Date"
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Invoice #, Receipt #, etc."
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Purpose of this entry"
            />
          </Grid>

          {/* Journal Lines */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1 }}>
              <Typography variant="subtitle2" color="primary">
                Journal Lines (Double-Entry)
              </Typography>
              <Button startIcon={<Add />} size="small" onClick={addLine}>
                Add Line
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell width="35%">Account</TableCell>
                    <TableCell width="25%">Line Description</TableCell>
                    <TableCell width="15%" align="right">Debit (Rs.)</TableCell>
                    <TableCell width="15%" align="right">Credit (Rs.)</TableCell>
                    <TableCell width="5%"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={accounts}
                          getOptionLabel={(option) => `${option.account_code} - ${option.account_name}`}
                          value={line.account}
                          onChange={(_, value) => updateLine(index, 'account', value)}
                          loading={loadingAccounts}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select account"
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: accountTypeColors[option.account_type] || '#666',
                                  }}
                                />
                                <Box>
                                  <Typography variant="body2">
                                    {option.account_code} - {option.account_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.account_type}
                                  </Typography>
                                </Box>
                              </Box>
                            </li>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Optional description"
                          value={line.description}
                          onChange={(e) => updateLine(index, 'description', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="0.00"
                          value={line.debit_amount || ''}
                          onChange={(e) => updateLine(index, 'debit_amount', parseFloat(e.target.value) || 0)}
                          disabled={line.credit_amount > 0}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{
                            '& input': {
                              textAlign: 'right',
                              bgcolor: line.debit_amount > 0 ? 'success.50' : 'inherit',
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="0.00"
                          value={line.credit_amount || ''}
                          onChange={(e) => updateLine(index, 'credit_amount', parseFloat(e.target.value) || 0)}
                          disabled={line.debit_amount > 0}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{
                            '& input': {
                              textAlign: 'right',
                              bgcolor: line.credit_amount > 0 ? 'error.50' : 'inherit',
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeLine(index)}
                          disabled={lines.length <= 2}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Totals Row */}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={2} align="right">
                      <Typography variant="subtitle2" fontWeight={600}>
                        Totals:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color={totals.isBalanced ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(totals.totalDebit)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color={totals.isBalanced ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(totals.totalCredit)}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Balance indicator */}
            {!totals.isBalanced && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Entry is not balanced. Difference: <strong>{formatCurrency(Math.abs(totals.difference))}</strong>
                  {totals.difference > 0 ? ' (Debits exceed Credits)' : ' (Credits exceed Debits)'}
                </Typography>
              </Alert>
            )}
          </Grid>

          {/* Quick Reference */}
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
              <Typography variant="subtitle2" color="info.main" gutterBottom>
                Double-Entry Quick Reference
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Debit increases:</strong> Assets, Expenses
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Credit increases:</strong> Liabilities, Equity, Income
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSubmit('DRAFT')}
          disabled={loading || !totals.isBalanced}
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
        >
          Save as Draft
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit('POSTED')}
          disabled={loading || !totals.isBalanced}
          startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        >
          Post Entry
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickJournalEntry;
